import { useTranslation } from 'react-i18next'
import type { RingVariant } from '../../../lib/calibration/useCalibrationSetPacer'
import type { TempoPhase } from '../../../lib/calibration/tempoConstants'

type TutMetronomeRingProps = {
  phase: TempoPhase
  variant: RingVariant
  isometricFlash: boolean
}

const RING_COLORS: Record<RingVariant, { stroke: string; glow: string }> = {
  default: { stroke: 'var(--kilo-accent)', glow: 'color-mix(in srgb, var(--kilo-accent) 45%, transparent)' },
  amber: { stroke: '#FFB300', glow: 'rgba(255,179,0,0.5)' },
  stall: { stroke: '#FF5252', glow: 'rgba(255,82,82,0.45)' },
}

const TutMetronomeRing = ({ phase, variant, isometricFlash }: TutMetronomeRingProps) => {
  const { t } = useTranslation()
  const colors = RING_COLORS[variant]
  const expanded =
    phase === 'isometric' ||
    phase === 'concentric' ||
    phase === 'await_confirm' ||
    isometricFlash

  const transitionClass =
    phase === 'eccentric'
      ? 'duration-[3000ms] ease-out'
      : phase === 'concentric'
        ? 'duration-1000 ease-in'
        : 'duration-75'

  const scale = expanded ? 1 : 0.52

  return (
    <div className="relative flex aspect-square w-full max-w-[min(88vw,340px)] items-center justify-center">
      <div
        className="absolute inset-0 rounded-full blur-3xl transition-opacity duration-300"
        style={{
          background: colors.glow,
          opacity: isometricFlash ? 1 : expanded ? 0.55 : 0.25,
        }}
      />

      <div
        className={`relative flex h-full w-full items-center justify-center rounded-full border-[6px] bg-black transition-transform ${transitionClass} ${
          isometricFlash ? 'bg-white/10' : ''
        }`}
        style={{
          transform: `scale(${scale})`,
          borderColor: isometricFlash ? '#ffffff' : colors.stroke,
          boxShadow: `0 0 40px ${colors.glow}, inset 0 0 24px ${colors.glow}`,
        }}
      >
        <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full -rotate-90">
          <circle
            cx="100"
            cy="100"
            r="88"
            fill="none"
            stroke={isometricFlash ? '#ffffff' : colors.stroke}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray="552"
            strokeDashoffset={expanded ? 0 : 180}
            className={transitionClass}
            style={{ transitionProperty: 'stroke-dashoffset, stroke' }}
          />
        </svg>

        <div className="relative z-10 text-center">
          <p className="font-mono text-sm font-bold uppercase tracking-[0.35em] text-white/70">
            {t(`workout.calibration.tracker.metronome.${phase}`)}
          </p>
          <p className="mt-1 font-mono text-xs uppercase tracking-widest text-neutral-500">3 · 0 · 1 · 0</p>
        </div>
      </div>
    </div>
  )
}

export default TutMetronomeRing
